import type { AssessmentPlan, CandidateStatus, JobConfig, JobInput, TaskLog } from '@eduvy/core';

/** Thin fetch wrapper for the recruiter API (same origin; the session is an httpOnly cookie). */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin', headers: {} };
  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    init.body = JSON.stringify(body);
    (init.headers as Record<string, string>)['content-type'] = 'application/json';
  }
  const res = await fetch(url, init);
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const message = (data as { error?: string } | null)?.error ?? `Request failed (${res.status})`;
    if (res.status === 401 && !url.includes('/auth/')) window.dispatchEvent(new Event('eduvy:signed-out'));
    throw new ApiError(message, res.status);
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body ?? {}),
  put: <T>(url: string, body: unknown) => request<T>('PUT', url, body),
  patch: <T>(url: string, body: unknown) => request<T>('PATCH', url, body),
  del: <T>(url: string) => request<T>('DELETE', url),
};

// ---- response shapes ----------------------------------------------------------------------

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'RECRUITER';
  /** The company this account belongs to: everything shown is that company's own. */
  orgId: string;
  orgName: string;
  /** Runs the platform: sees the Companies page and the provider details. */
  platformAdmin: boolean;
}

/** A company's plan, as the platform set it. Money in paise. */
export interface Plan {
  planName: string;
  includedInterviews: number;
  pricePerInterview: number;
  monthlyFee: number;
}

export interface Usage {
  interviews: number;
  minutes: number;
}

/** A month's usage priced on the plan. */
export interface Quote extends Usage {
  includedInterviews: number;
  extraInterviews: number;
  monthlyFee: number;
  usageFee: number;
  total: number;
}

export interface UsageMonth {
  year: number;
  month: number;
  usage: Usage;
}

export interface InvoiceView {
  id: string;
  number: string;
  periodStart: string;
  periodEnd: string;
  interviews: number;
  minutes: number;
  monthlyFee: number;
  usageFee: number;
  total: number;
  status: 'ISSUED' | 'PAID' | 'VOID';
  note: string | null;
  issuedAt: string;
  paidAt: string | null;
}

/** GET /api/org/billing: a company's own plan, month and invoices. */
export interface OrgBilling {
  plan: Plan;
  billingEmail: string | null;
  period: { year: number; month: number };
  month: Quote;
  history: UsageMonth[];
  invoices: InvoiceView[];
}

/** A company on the platform, as its operators see it. */
export interface Company {
  id: string;
  name: string;
  active: boolean;
  createdAt: string;
  users: number;
  jobs: number;
  candidates: number;
  plan: Plan;
  last30Days: { interviews: number; minutes: number };
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'RECRUITER';
  active: boolean;
  platformAdmin: boolean;
  createdAt: string;
}

/** GET /api/orgs/:id: one company as the platform sees it. */
export interface CompanyDetail {
  id: string;
  name: string;
  active: boolean;
  createdAt: string;
  plan: Plan;
  billingEmail: string | null;
  billingNotes: string | null;
  team: TeamMember[];
  jobs: number;
  candidates: number;
  period: { year: number; month: number };
  month: Quote;
  history: UsageMonth[];
  invoices: InvoiceView[];
}

/** GET /api/admin/overview: every company's month in one view. */
export interface AdminOverview {
  period: { year: number; month: number };
  companies: Array<{ id: string; name: string; active: boolean; createdAt: string; users: number; jobs: number; plan: Plan; month: Usage; quote: Quote }>;
  totals: { companies: number; active: number; interviews: number; minutes: number; liveNow: number; expected: number; outstanding: number; outstandingCount: number; paidThisMonth: number };
}

export interface JobSummary {
  id: string;
  title: string;
  department: string | null;
  location: string;
  workMode: string;
  status: 'DRAFT' | 'ACTIVE' | 'CLOSED';
  outreachMode: JobInput['outreachMode'];
  createdAt: string;
  counts: { total: number; byStatus: Partial<Record<CandidateStatus, number>> };
}

export interface ImportBatchSummary {
  id: string;
  filename: string;
  totalRows: number;
  imported: number;
  duplicates: number;
  invalid: number;
  createdAt: string;
}

export interface Job extends Omit<JobSummary, 'counts'> {
  description: string;
  config: JobConfig;
  interviewMinutes: number;
  maxCallAttempts: number;
  retryAfterMinutes: number;
  linkValidityHours: number;
  counts: { byStatus: Partial<Record<CandidateStatus, number>> };
  imports: ImportBatchSummary[];
  /** The public apply page's address (valid while config.apply.enabled). */
  applyUrl: string | null;
}

export type InterviewStatus =
  | 'PENDING'
  | 'INVITED'
  | 'RINGING'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'NO_ANSWER'
  | 'BUSY'
  | 'FAILED'
  | 'ABANDONED'
  | 'DECLINED'
  | 'EXPIRED'
  | 'CANCELLED';

export type Channel = 'WEB' | 'VIDEO' | 'PHONE';

/** The rounds: the AI interview, the AI skills assessment, the recruiter's own interview. */
export type Stage = 'SCREENING' | 'ASSESSMENT' | 'RECRUITER';

export type DecisionKind = 'SHORTLIST' | 'REJECT' | 'HOLD' | 'ADVANCE' | 'SELECT';

export type Verdict = 'QUALIFIED' | 'NOT_QUALIFIED' | 'NEEDS_REVIEW';
export type Recommendation = 'STRONG_YES' | 'YES' | 'MAYBE' | 'NO';

export interface CandidateRow {
  id: string;
  name: string;
  email: string | null;
  phoneE164: string | null;
  experienceYears: number | null;
  experienceRaw: string | null;
  sector: string | null;
  status: CandidateStatus;
  preScreen: { flags: string[] } | null;
  createdAt: string;
  latest: {
    id: string;
    stage: Stage;
    channel: Channel;
    status: InterviewStatus;
    attemptNo: number;
    startedAt: string | null;
    endedAt: string | null;
    endReason: string | null;
    link: string | null;
    /** Booked slot for a scheduled (video) interview, when the scheduling work lands. */
    scheduledAt?: string | null;
  } | null;
  score: {
    overallScore: number;
    verdict: Verdict;
    recommendation: Recommendation;
    summary: string;
    /** Why the verdict: the rules that fired, or the score against the bar. */
    reasons: string[];
  } | null;
  /** The skills assessment's score (stage 2), once scored. */
  skills: { overallScore: number; verdict: Verdict; recommendation: Recommendation; summary: string; reasons: string[] } | null;
  /** The latest recruiter decision. */
  decision: { kind: DecisionKind; reason: string | null; createdAt: string } | null;
}

export interface Turn {
  id: string;
  seq: number;
  speaker: 'AI' | 'CANDIDATE' | 'SYSTEM';
  text: string;
  language: string | null;
  offsetMs: number;
  durationMs: number | null;
  interrupted: boolean;
  typed: boolean;
  audioUrl: string | null;
}

export interface KnockoutResult {
  fact: string;
  message: string;
  severity: 'hard' | 'soft';
  outcome: 'passed' | 'failed' | 'unknown';
  value: number | boolean | string | null;
}

export interface Assessment {
  competencies: Array<{
    key: string;
    score: number | null;
    confidence: 'low' | 'medium' | 'high';
    evidence: Array<{ quote: string; turn: number }>;
    rationale: string;
  }>;
  facts: Array<{ key: string; value: string | null; turn: number | null; note: string | null }>;
  typedFacts: Record<string, number | boolean | string | null>;
  englishLevel: string;
  primaryLanguage: string;
  summary: string;
  strengths: string[];
  concerns: string[];
  redFlags: string[];
  openQuestions: string[];
  completeness: 'complete' | 'partial' | 'insufficient';
  reasons: string[];
  coverage: number;
  integrity: Record<string, number>;
}

/** The skills assessment's report (a Scorecard's assessment when its interview is the ASSESSMENT stage). */
export interface SkillsReport {
  kind: 'skills';
  tasks: Array<{ taskId: string; score: number | null; languageFluency: number | null; evidence: Array<{ quote: string; turn: number }>; rationale: string }>;
  scored: Array<{ taskId: string; n: number; title: string; kind: string; language: string; score: number | null; languageFluency: number | null }>;
  summary: string;
  strengths: string[];
  concerns: string[];
  integrityNotes: string[];
  reasons: string[];
  coverage: number;
  integrity: Record<string, number>;
}

export interface Scorecard {
  id: string;
  model: string;
  overallScore: number;
  recommendation: Recommendation;
  aiRecommendation: Recommendation;
  verdict: Verdict;
  assessment: Assessment;
  knockouts: KnockoutResult[];
  summary: string;
  createdAt: string;
}

export interface InterviewDetail {
  id: string;
  stage: Stage;
  channel: Channel;
  /** The skills assessment: the tasks planned for this candidate. */
  plan: AssessmentPlan | null;
  /** Whole-interview recording (audio, or video for VIDEO interviews), when one was kept. */
  recordingUrl?: string | null;
  scheduledAt?: string | null;
  status: InterviewStatus;
  attemptNo: number;
  language: string | null;
  consentAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  endReason: string | null;
  inviteSentAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  state: { facts?: Record<string, string>; flags?: string[]; sectionsDone?: string[]; tasks?: TaskLog[] } | null;
  integrity: Record<string, number> | null;
  link: string | null;
  turns: Turn[];
  scorecard: Scorecard | null;
}

export interface CandidateDetail {
  id: string;
  name: string;
  email: string | null;
  phoneE164: string | null;
  phoneRaw: string | null;
  experienceYears: number | null;
  experienceRaw: string | null;
  sector: string | null;
  source: string | null;
  resumeText: string | null;
  extra: Record<string, string> | null;
  preScreen: { flags: string[] } | null;
  status: CandidateStatus;
  preferredLang: string | null;
  erasedAt: string | null;
  createdAt: string;
  job: { id: string; title: string; config: JobConfig; interviewMinutes: number };
  interviews: InterviewDetail[];
  decisions: Array<{ id: string; kind: DecisionKind; reason: string | null; createdAt: string; byUser: { name: string } }>;
}

export interface RowOutcome {
  row: number;
  status: 'imported' | 'duplicate' | 'invalid';
  name?: string | null;
  reason?: string;
  duplicateOf?: string;
  warnings?: string[];
  lead?: { name: string };
}

export interface ImportSummary {
  batchId: string;
  totalRows: number;
  imported: number;
  duplicates: number;
  invalid: number;
  unmappedColumns: string[];
  outcomes: RowOutcome[];
}

export interface SettingsStatus {
  calling: { window: string; days: string };
  liveSessions: number;
  /** Platform admins only: what works, and the providers behind it. */
  ready?: { ai: boolean; voice: boolean; phone: boolean; email: boolean };
  providers?: {
    llm: { provider: string; interviewModel: string; scoringModel: string };
    voice: { provider: string; stt: string; tts: string; speaker: string; voices?: string };
    telephony: { provider: string; maxConcurrent: number };
    publicBaseUrl: string;
  };
}

export interface LiveInterview {
  id: string;
  channel: Channel;
  status: InterviewStatus;
  startedAt: string | null;
  candidate: { id: string; name: string; job: { id: string; title: string } };
}
